import { fromPromise, type NextLink, type Operation } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { captureException, captureMessage, withScope } from '@sentry/react';
import type { Scope, SeverityLevel } from '@sentry/types';
import type { GraphQLError } from 'graphql';
import { toast } from 'react-toastify';

import UserManagementServiceImpl from 'services/userManagement/userManagementServiceImpl';
import { type ClubSparkAuthenticationInterface } from 'typedefs/authTypes';

let isRefreshing = false;
let pendingRequests: any[] = [];

const setIsRefreshing = (value: boolean) => {
  isRefreshing = value;
};

const addPendingRequest = (pendingRequest: any) => {
  pendingRequests.push(pendingRequest);
};

const resolvePendingRequests = () => {
  pendingRequests.forEach((callback) => callback());
  pendingRequests = [];
};

const attachTokenToOperation = (operation: Operation): Operation => {
  const oldHeaders = operation.getContext().headers;
  const authUser = JSON.parse(localStorage.getItem('authenticatedUser')) as ClubSparkAuthenticationInterface;
  const token = authUser?.metadata?.token;
  operation.setContext({
    headers: {
      ...oldHeaders,
      Authorization: token,
    },
  });
  return operation;
};

const handleUnauthorizedError = (forward: NextLink, operation: Operation) => {
  // TODO: make it injectable when we add injectable services
  const userManagementService = UserManagementServiceImpl;

  if (!isRefreshing) {
    setIsRefreshing(true);
    return fromPromise(
      userManagementService.getCurrentUser().catch(() => {
        userManagementService.signOut();
      }),
    ).flatMap(() => {
      const updatedOperation = attachTokenToOperation(operation);
      resolvePendingRequests();
      setIsRefreshing(false);
      return forward(updatedOperation);
    });
  }

  return fromPromise(
    new Promise((resolve) => {
      addPendingRequest(resolve);
    }),
  ).flatMap(() => {
    const updatedOperation = attachTokenToOperation(operation);
    return forward(updatedOperation);
  });
};

const handleGraphQLErrorError = (error: GraphQLError) => {
  if (error?.message?.includes('SPLSwiftServerCore.RepositoryError.objectNotFound')) {
    return; // a shameful workaround for Dual Matches that don't exist in TS
  }

  toast.error(error?.message);
};

const setSentryGqlScope = (scope: Scope, operation: Operation) => {
  scope.setTransactionName(operation.operationName);
  scope.setLevel('error' as SeverityLevel);
  scope.setExtra('query', operation.query.loc?.source.body);
  scope.setExtra('variables', JSON.stringify(operation.variables));
};

export const errorLink = onError(({ graphQLErrors, networkError, forward, operation }) => {
  if (graphQLErrors) {
    for (const error of graphQLErrors) {
      withScope((scope) => {
        setSentryGqlScope(scope, operation);
        captureMessage(error.message);
      });

      if (error.extensions.code === 'UNAUTHENTICATED') return handleUnauthorizedError(forward, operation);
      return handleGraphQLErrorError(error);
    }
  } else if (networkError) {
    withScope((scope) => {
      setSentryGqlScope(scope, operation);
      captureException(networkError);
    });
  }
});
